/**
 * Expiry Activity Log tab — shows point expiry deductions and tier changes.
 *
 * Feature 1 — OPS-1836 (Point Expiry Activity Log)
 */

import { useState } from "react";
import { ArrowDown, Minus, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useExpiryActivityLog, type ExpiryLogEntry } from "../api/rewardsAdminHooks";

const PAGE_SIZE = 20;

function LogIcon({ type }: { type: ExpiryLogEntry["type"] }) {
  if (type === "tier_change") {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100">
        <ArrowDown className="h-4 w-4 text-orange-600" />
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100">
      <Minus className="h-4 w-4 text-red-600" />
    </div>
  );
}

export function ExpiryLogTab() {
  const [offset, setOffset] = useState(0);
  const logQuery = useExpiryActivityLog(PAGE_SIZE, offset);

  if (logQuery.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    );
  }

  const entries = logQuery.data?.entries ?? [];
  const hasMore = logQuery.data?.has_more ?? false;

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          ยังไม่มีกิจกรรมการหมดอายุ
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-4">
      {entries.map((entry, idx) => (
        <div
          key={`${entry.date}-${idx}`}
          className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
        >
          <LogIcon type={entry.type} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {entry.customer_name}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {entry.description}
            </p>
          </div>
          {entry.points > 0 && (
            <Badge variant="destructive" className="text-xs shrink-0">
              -{entry.points.toLocaleString()} แต้ม
            </Badge>
          )}
          {entry.type === "tier_change" && (
            <Badge variant="outline" className="text-xs shrink-0 border-orange-300 text-orange-700">
              เปลี่ยนเทียร์
            </Badge>
          )}
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {entry.date}
          </span>
        </div>
      ))}

      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
          disabled={logQuery.isFetching}
          className="w-full"
        >
          {logQuery.isFetching ? "กำลังโหลด..." : "โหลดเพิ่มเติม"}
        </Button>
      )}
    </div>
  );
}
